import './App.css';
import React, { useEffect, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import i18n from './i18n/i18n';
import { LanguageProvider } from './i18n/LanguageContext';
import 'react-toastify/dist/ReactToastify.css';
import { ToastContainer } from 'react-toastify';
import ProtectedAdminRoute from './components/ProtectedAdminRoute';
import CreateLegacyRedirect from './components/CreateLegacyRedirect';
import ErrorBoundary from './components/ErrorBoundary';
import GlobalErrorHandler from './components/GlobalErrorHandler';
import { CreateCourseChoiceProvider } from './context/CreateCourseChoiceContext';
import { OrganizationProvider } from './shared/branding/OrganizationContext';
import ImpersonationBanner from './shared/branding/ImpersonationBanner';
import { RequireAuth } from './shared/auth/RequireAuth';
import { getSessionAuth } from './shared/auth/session';

// Keep critical pages loaded immediately
import Landing from './pages/landing';
import SignIn from './pages/signin';
import SignUp from './pages/signup';
import OrganizationStart from './pages/organizationStart';
import CourseIdeas from './pages/courseIdeas';
import FAQ from './pages/faq';
import logger from './utils/logger';
import { preloadPolicies } from './utils/policies';
import ConnectivityBanner from './components/ConnectivityBanner';
import CookieConsentBanner from './components/CookieConsentBanner';
import AnalyticsRouteTracker from './components/AnalyticsRouteTracker';
import OnScreenStatusBar from './components/OnScreenStatusBar';
import lazyWithRetry from './utils/lazyWithRetry';

function PreserveRedirect({ to }) {
  const location = useLocation();
  return (
    <Navigate
      to={{ pathname: to, search: location.search, hash: location.hash }}
      state={location.state}
      replace
    />
  );
}

/** Send create/genui entry URLs to Studio for instructors, Learn for students. */
function RoleAwareCreateRedirect({ studioPath, learnPath }) {
  const location = useLocation();
  const auth = getSessionAuth();
  const canStudio = Boolean(
    auth.platformAdmin
    || auth.orgRole === 'instructor'
    || auth.orgRole === 'org_admin'
    || auth.role === 'instructor'
    || auth.role === 'teacher'
    || auth.role === 'admin'
  );
  const to = canStudio ? studioPath : learnPath;
  return (
    <Navigate
      to={{ pathname: to, search: location.search, hash: location.hash }}
      state={location.state}
      replace
    />
  );
}

function canUseStudioShell(auth = getSessionAuth()) {
  return Boolean(
    auth.platformAdmin
    || auth.orgRole === 'instructor'
    || auth.orgRole === 'org_admin'
    || auth.role === 'instructor'
    || auth.role === 'teacher'
    || auth.role === 'admin'
  );
}

/** Legacy /projects → studio for teachers, learn for students. */
function LegacyProjectsListRedirect() {
  const location = useLocation();
  const to = canUseStudioShell() ? '/studio/projects' : '/learn/projects';
  return (
    <Navigate
      to={{ pathname: to, search: location.search, hash: location.hash }}
      state={location.state}
      replace
    />
  );
}

function LegacyProjectRedirect({ suffix = '' }) {
  const { id } = useParams();
  const location = useLocation();
  const base = canUseStudioShell() ? '/studio/projects' : '/learn/projects';
  return (
    <Navigate
      to={{ pathname: `${base}/${id}${suffix}`, search: location.search, hash: location.hash }}
      state={location.state}
      replace
    />
  );
}

function LegacyClassesRedirect() {
  const location = useLocation();
  const auth = getSessionAuth();
  const to = canUseStudioShell(auth) ? '/studio/classes' : '/learn/classes';
  return <Navigate to={{ pathname: to, search: location.search }} state={location.state} replace />;
}

// Lazy load heavy components for better performance
const Home = lazyWithRetry(() => import('./pages/home'));
const Topics = lazyWithRetry(() => import('./pages/topics'));
const Course = lazyWithRetry(() => import('./pages/course'));
const Profile = lazyWithRetry(() => import('./pages/profile'));
const Pricing = lazyWithRetry(() => import('./pages/pricing'));
const About = lazyWithRetry(() => import('./pages/about'));
const Features = lazyWithRetry(() => import('./pages/features'));
const Platform = lazyWithRetry(() => import('./pages/platform'));
const Contact = lazyWithRetry(() => import('./pages/contact'));
const ForgotPassword = lazyWithRetry(() => import('./pages/forgotpassword'));
const ResetPassword = lazyWithRetry(() => import('./pages/resetpassword'));
const Success = lazyWithRetry(() => import('./pages/success'));
const Support = lazyWithRetry(() => import('./pages/support'));
const Payment = lazyWithRetry(() => import('./pages/payment'));
const Marketplace = lazyWithRetry(() => import('./pages/marketplace'));
const MarketplaceCourseDetail = lazyWithRetry(() => import('./pages/marketplaceCourseDetail'));
const Manage = lazyWithRetry(() => import('./pages/manage'));
const Shareable = lazyWithRetry(() => import('./pages/shareable'));
const StoryTeller = lazyWithRetry(() => import('./pages/StoryTeller'));
const Exam = lazyWithRetry(() => import('./pages/exam'));
const Pending = lazyWithRetry(() => import('./pages/pending'));
const Successful = lazyWithRetry(() => import('./pages/successful'));
const Certificate = lazyWithRetry(() => import('./pages/certificate'));
const TopicGeneration = lazyWithRetry(() => import('./pages/topicGeneration'));
const ContentGeneration = lazyWithRetry(() => import('./pages/contentGeneration'));
const DocumentUpload = lazyWithRetry(() => import('./pages/documentUpload'));
const AICourseCreation = lazyWithRetry(() => import('./pages/aiCourseCreation'));
const Projects = lazyWithRetry(() => import('./pages/projects'));
const ProjectDetail = lazyWithRetry(() => import('./pages/projectDetail'));
const StudyMode = lazyWithRetry(() => import('./pages/studyMode'));
const MyClasses = lazyWithRetry(() => import('./pages/myClasses'));
const MyCertificates = lazyWithRetry(() => import('./pages/myCertificates'));
const GenuiPlayground = lazyWithRetry(() => import('./pages/genuiPlayground'));
const CreateCourseChoice = lazyWithRetry(() => import('./pages/createCourseChoice'));
const CourseReview = lazyWithRetry(() => import('./pages/courseReview'));
const InstructorAnalytics = lazyWithRetry(() => import('./pages/instructorAnalytics'));
const MicrolearningSession = lazyWithRetry(() => import('./components/MicrolearningSession'));

// Role shells + super admin (legacy /admin redirects below)
const OrgAdminHome = lazyWithRetry(() => import('./apps/orgadmin/OrgAdminHome'));
const OrgAdminLayout = lazyWithRetry(() => import('./apps/orgadmin/OrgAdminLayout'));
const OrgMembersPage = lazyWithRetry(() => import('./apps/orgadmin/OrgMembersPage'));
const OrgDomainsPage = lazyWithRetry(() => import('./apps/orgadmin/OrgDomainsPage'));
const OrgBrandingPage = lazyWithRetry(() => import('./apps/orgadmin/OrgBrandingPage'));
const OrgBillingPage = lazyWithRetry(() => import('./apps/orgadmin/OrgBillingPage'));
const OrgUsagePage = lazyWithRetry(() => import('./apps/orgadmin/OrgUsagePage'));
const OrgSsoPage = lazyWithRetry(() => import('./apps/orgadmin/OrgSsoPage'));
const OrgPoliciesPage = lazyWithRetry(() => import('./apps/orgadmin/OrgPoliciesPage'));
const OrgCoursesPage = lazyWithRetry(() => import('./apps/orgadmin/OrgCoursesPage'));
const OrgSettingsPage = lazyWithRetry(() => import('./apps/orgadmin/OrgSettingsPage'));
const InstructorLayout = lazyWithRetry(() => import('./apps/instructor/InstructorLayout'));
const StudioHome = lazyWithRetry(() => import('./apps/instructor/StudioHome'));
const LearnerLayout = lazyWithRetry(() => import('./apps/learner/LearnerLayout'));
const SuperAdminOrganizations = lazyWithRetry(() => import('./apps/superadmin/OrganizationsPage'));
const SuperAdminLayout = lazyWithRetry(() => import('./apps/superadmin/SuperAdminLayout'));
const PlatformDashboard = lazyWithRetry(() => import('./apps/superadmin/PlatformDashboard'));
const PlatformUsagePage = lazyWithRetry(() => import('./apps/superadmin/PlatformUsagePage'));
const PlatformAuditPage = lazyWithRetry(() => import('./apps/superadmin/PlatformAuditPage'));
const PlatformSystemPage = lazyWithRetry(() => import('./apps/superadmin/PlatformSystemPage'));
const PlatformDomainsPage = lazyWithRetry(() => import('./apps/superadmin/PlatformDomainsPage'));
const OrganizationDetailPage = lazyWithRetry(() => import('./apps/superadmin/OrganizationDetailPage'));
const SuperAdminUsersPage = lazyWithRetry(() => import('./apps/superadmin/SuperAdminUsersPage'));
const SuperAdminCoursesPage = lazyWithRetry(() => import('./apps/superadmin/SuperAdminCoursesPage'));
const SuperAdminContactsPage = lazyWithRetry(() => import('./apps/superadmin/SuperAdminContactsPage'));
const SuperAdminPricingPage = lazyWithRetry(() => import('./apps/superadmin/SuperAdminPricingPage'));
const SuperAdminPaidUsersPage = lazyWithRetry(() => import('./apps/superadmin/SuperAdminPaidUsersPage'));
const SuperAdminAdminsPage = lazyWithRetry(() => import('./apps/superadmin/SuperAdminAdminsPage'));
const SuperAdminLegalPage = lazyWithRetry(() => import('./apps/superadmin/SuperAdminLegalPage'));
const SsoCompletePage = lazyWithRetry(() => import('./pages/ssoComplete'));
const LearnerCatalogPage = lazyWithRetry(() => import('./apps/learner/LearnerCatalogPage'));
const LearnerAnalyticsPage = lazyWithRetry(() => import('./apps/learner/LearnerAnalyticsPage'));

// Policy pages
const TermsPolicy = lazyWithRetry(() => import('./pages/termspolicy'));
const PrivacyPolicy = lazyWithRetry(() => import('./pages/privacypolicy'));
const RefundPolicy = lazyWithRetry(() => import('./pages/refundpolicy'));
const BillingPolicy = lazyWithRetry(() => import('./pages/billingpolicy'));
const CancelPolicy = lazyWithRetry(() => import('./pages/cancelpolicy'));
const Failed = lazyWithRetry(() => import('./pages/failed'));
const Desktop = lazyWithRetry(() => import('./pages/desktop'));
const Blog = lazyWithRetry(() => import('./pages/blog'));
const Careers = lazyWithRetry(() => import('./pages/careers'));
const CookiePolicy = lazyWithRetry(() => import('./pages/cookiepolicy'));
const Gdpr = lazyWithRetry(() => import('./pages/gdpr'));
const AiTransparency = lazyWithRetry(() => import('./pages/aiTransparency'));
const LegalNotice = lazyWithRetry(() => import('./pages/legalnotice'));
const Help = lazyWithRetry(() => import('./pages/help'));
const Documentation = lazyWithRetry(() => import('./pages/documentation'));
const Community = lazyWithRetry(() => import('./pages/community'));
const Status = lazyWithRetry(() => import('./pages/status'));

// Error page
const Error = lazyWithRetry(() => import('./pages/error'));

function App() {
  useEffect(() => {
    // Stale service workers can cache old JS bundles and hide new routes in local/dev.
    if (typeof window !== 'undefined' && 'serviceWorker' in navigator) {
      const host = window.location.hostname;
      if (host === 'localhost' || host === '127.0.0.1') {
        navigator.serviceWorker.getRegistrations().then((regs) => {
          regs.forEach((reg) => reg.unregister());
        }).catch(() => {});
        if (window.caches?.keys) {
          caches.keys().then((keys) => Promise.all(keys.map((k) => caches.delete(k)))).catch(() => {});
        }
      }
    }

    initializeServices();
    preloadPolicies();
  }, []);

  const initializeServices = async () => {
    try {
      // Initialize offline service
      await import('./services/offlineService');
      
      // Initialize push notifications with better error handling
      try {
        const PushNotificationService = await import('./services/pushNotificationService');
        const success = await PushNotificationService.default.initialize();
        if (success) {
          logger.info('✅ Push notifications initialized');
        }
      } catch (error) {
        logger.warn('⚠️ Push notifications not available:', error.message);
      }
      
      // PWA service not available
      
    } catch (error) {
      logger.error('Error initializing services:', error);
    }
  };

  return (
    <GlobalErrorHandler>
      <LanguageProvider>
        <OrganizationProvider>
        <Router>
          <CreateCourseChoiceProvider>
          <div>
            <ImpersonationBanner />
            <ConnectivityBanner />
            <CookieConsentBanner />
            <AnalyticsRouteTracker />
            {/* In-layout status (analysis, saves, errors) — preferred over corner toasts */}
            <OnScreenStatusBar />

            {/* Fallback for rare React-node toast content; string toasts go on-screen */}
            <ToastContainer
              position="top-center"
              autoClose={4000}
              hideProgressBar
              newestOnTop
              closeOnClick
              pauseOnFocusLoss
              draggable={false}
              pauseOnHover
              theme="colored"
              limit={2}
              style={{ zIndex: 99999, top: '4.5rem' }}
              toastClassName="onscreen-toast-fallback"
            />

            <ErrorBoundary>
              <Suspense fallback={
                <div className="min-h-screen flex items-center justify-center bg-slate-100 dark:bg-slate-950 px-4">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-2 border-indigo-200 dark:border-slate-700 border-t-indigo-600 dark:border-t-indigo-400 mx-auto mb-4" aria-hidden />
                    <p className="text-slate-700 dark:text-slate-300 text-sm font-medium">{i18n.t('common.loading')}</p>
                  </div>
                </div>
              }>
            <Routes>
              {/* Public Routes */}
              <Route path="/" element={<Landing />} />
              <Route path="/signin" element={<SignIn />} />
              <Route path="/signup" element={<SignUp />} />
              <Route path="/organizations/start" element={<OrganizationStart />} />
              <Route path="/course-ideas" element={<CourseIdeas />} />
              <Route path="/faq" element={<FAQ />} />
              <Route path="/about" element={<About />} />
              <Route path="/platform" element={<Platform />} />
              <Route path="/features" element={<Features />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/forgot" element={<ForgotPassword />} />
              <Route path="/reset-password/:token" element={<ResetPassword />} />
              <Route path="/reset/:token" element={<ResetPassword />} />
              <Route path="/reset" element={<ResetPassword />} />
              <Route path="/success" element={<Success />} />
              <Route path="/support" element={<Support />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/marketplace" element={<Marketplace />} />
              <Route path="/marketplace/course/:id" element={<MarketplaceCourseDetail />} />
              <Route path="/shareable/:id" element={<Shareable />} />
              <Route path="/certificate" element={<Certificate />} />
              <Route path="/topic-generation" element={<TopicGeneration />} />
              <Route path="/content-generation" element={<ContentGeneration />} />
              <Route path="/document-upload" element={<DocumentUpload />} />
              <Route path="/ai-course-creation" element={<AICourseCreation />} />
              <Route path="/projects" element={<LegacyProjectsListRedirect />} />
              <Route path="/projects/:id" element={<LegacyProjectRedirect />} />
              <Route path="/projects/:id/study" element={<LegacyProjectRedirect suffix="/study" />} />
              <Route path="/my-classes" element={<LegacyClassesRedirect />} />
              <Route path="/my-certificates" element={<PreserveRedirect to="/learn/certificates" />} />
              <Route path="/genui" element={<RoleAwareCreateRedirect studioPath="/studio/genui" learnPath="/learn/genui" />} />
              <Route path="/create-choice" element={<RoleAwareCreateRedirect studioPath="/studio/create-choice" learnPath="/learn/create-choice" />} />
              <Route path="/create" element={<CreateLegacyRedirect />} />
              <Route path="/course-review" element={<PreserveRedirect to="/studio/review" />} />
              <Route path="/terms" element={<TermsPolicy />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/refund" element={<RefundPolicy />} />
              <Route path="/billing" element={<BillingPolicy />} />
              <Route path="/cancellation" element={<CancelPolicy />} />
              <Route path="/desktop" element={<Desktop />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/careers" element={<Careers />} />
              <Route path="/cookies" element={<CookiePolicy />} />
              <Route path="/gdpr" element={<Gdpr />} />
              <Route path="/ai-transparency" element={<AiTransparency />} />
              <Route path="/legal" element={<LegalNotice />} />
              <Route path="/help" element={<Help />} />
              <Route path="/docs" element={<Documentation />} />
              <Route path="/community" element={<Community />} />
              <Route path="/status" element={<Status />} />
              <Route path="/failed" element={<Failed />} />

              {/* Protected Routes (legacy URLs → shells) */}
              <Route path="/topics" element={<Topics />} />
              <Route path="/course" element={<PreserveRedirect to="/learn/course" />} />
              <Route path="/profile" element={<PreserveRedirect to="/learn/profile" />} />
              <Route path="/payment" element={<Payment />} />
              <Route path="/manage" element={<Manage />} />
              <Route path="/subscription" element={<Manage />} />
              <Route path="/storyteller" element={<StoryTeller />} />
              <Route path="/exam" element={<Exam />} />
              <Route path="/pending" element={<Pending />} />
              <Route path="/successful" element={<Successful />} />
              <Route path="/microlearning" element={<PreserveRedirect to="/learn/practice" />} />
              <Route path="/practice" element={<PreserveRedirect to="/learn/practice" />} />

              {/* Instructor studio shell */}
              <Route path="/studio" element={
                <RequireAuth roles={['instructor', 'org_admin', 'admin', 'teacher', 'platform_admin']}>
                  <InstructorLayout />
                </RequireAuth>
              }>
                <Route index element={<StudioHome />} />
                <Route path="projects" element={<Projects />} />
                <Route path="projects/:id" element={<ProjectDetail />} />
                <Route path="projects/:id/study" element={<ErrorBoundary><StudyMode /></ErrorBoundary>} />
                <Route path="study" element={<Projects />} />
                <Route path="genui" element={<ErrorBoundary><GenuiPlayground /></ErrorBoundary>} />
                <Route path="analytics" element={<InstructorAnalytics />} />
                <Route path="classes" element={<MyClasses />} />
                <Route path="ideas" element={<CourseIdeas />} />
                <Route path="review" element={<CourseReview />} />
                <Route path="create-choice" element={<CreateCourseChoice />} />
                <Route path="exam" element={<Exam />} />
              </Route>

              {/* Learner shell */}
              <Route path="/learn" element={
                <RequireAuth>
                  <LearnerLayout />
                </RequireAuth>
              }>
                <Route index element={<Home />} />
                <Route path="analytics" element={<LearnerAnalyticsPage />} />
                <Route path="classes" element={<MyClasses />} />
                <Route path="certificates" element={<MyCertificates />} />
                <Route path="catalog" element={<LearnerCatalogPage />} />
                <Route path="projects" element={<Projects />} />
                <Route path="projects/:id" element={<ProjectDetail />} />
                <Route path="projects/:id/study" element={<ErrorBoundary><StudyMode /></ErrorBoundary>} />
                <Route path="study" element={<Projects />} />
                <Route path="create-choice" element={<CreateCourseChoice />} />
                <Route path="genui" element={<ErrorBoundary><GenuiPlayground /></ErrorBoundary>} />
                <Route path="course" element={<ErrorBoundary><Course /></ErrorBoundary>} />
                <Route path="profile" element={<Profile />} />
                <Route path="subscription" element={<Manage />} />
                <Route path="exam" element={<Exam />} />
                <Route path="practice" element={<MicrolearningSession />} />
                <Route path="microlearning" element={<PreserveRedirect to="/learn/practice" />} />
              </Route>

              {/* Org admin shell */}
              <Route path="/org/admin" element={
                <RequireAuth roles={['org_admin', 'platform_admin']}>
                  <OrgAdminLayout />
                </RequireAuth>
              }>
                <Route index element={<OrgAdminHome />} />
                <Route path="members" element={<OrgMembersPage />} />
                <Route path="courses" element={<OrgCoursesPage />} />
                <Route path="domains" element={<OrgDomainsPage />} />
                <Route path="branding" element={<OrgBrandingPage />} />
                <Route path="policies" element={<OrgPoliciesPage />} />
                <Route path="usage" element={<OrgUsagePage />} />
                <Route path="billing" element={<OrgBillingPage />} />
                <Route path="settings" element={<OrgSettingsPage />} />
                <Route path="sso" element={<OrgSsoPage />} />
              </Route>

              <Route path="/auth/sso/complete" element={<SsoCompletePage />} />

              {/* Legacy path aliases → new shells */}
              <Route path="/home" element={<PreserveRedirect to="/learn" />} />

              {/* Legacy /admin → /super-admin (feature parity in new shell) */}
              <Route path="/admin" element={<PreserveRedirect to="/super-admin" />} />
              <Route path="/admin/dashboard" element={<PreserveRedirect to="/super-admin" />} />
              <Route path="/admin/users" element={<PreserveRedirect to="/super-admin/users" />} />
              <Route path="/admin/courses" element={<PreserveRedirect to="/super-admin/courses" />} />
              <Route path="/admin/contacts" element={<PreserveRedirect to="/super-admin/contacts" />} />
              <Route path="/admin/pricing" element={<PreserveRedirect to="/super-admin/pricing" />} />
              <Route path="/admin/paidusers" element={<PreserveRedirect to="/super-admin/paid-users" />} />
              <Route path="/admin/admins" element={<PreserveRedirect to="/super-admin/admins" />} />
              <Route path="/admin/billing" element={<PreserveRedirect to="/super-admin/legal" />} />
              <Route path="/admin/terms" element={<PreserveRedirect to="/super-admin/legal" />} />
              <Route path="/admin/privacy" element={<PreserveRedirect to="/super-admin/legal" />} />
              <Route path="/admin/cookies" element={<PreserveRedirect to="/super-admin/legal" />} />
              <Route path="/admin/gdpr" element={<PreserveRedirect to="/super-admin/legal" />} />
              <Route path="/admin/legal" element={<PreserveRedirect to="/super-admin/legal" />} />
              <Route path="/admin/cancellation" element={<PreserveRedirect to="/super-admin/legal" />} />
              <Route path="/admin/refund" element={<PreserveRedirect to="/super-admin/legal" />} />

              <Route path="/super-admin" element={
                <ProtectedAdminRoute>
                  <SuperAdminLayout />
                </ProtectedAdminRoute>
              }>
                <Route index element={<PlatformDashboard />} />
                <Route path="organizations" element={<SuperAdminOrganizations />} />
                <Route path="organizations/:id" element={<OrganizationDetailPage />} />
                <Route path="usage" element={<PlatformUsagePage />} />
                <Route path="audit" element={<PlatformAuditPage />} />
                <Route path="domains" element={<PlatformDomainsPage />} />
                <Route path="system" element={<PlatformSystemPage />} />
                <Route path="users" element={<SuperAdminUsersPage />} />
                <Route path="courses" element={<SuperAdminCoursesPage />} />
                <Route path="contacts" element={<SuperAdminContactsPage />} />
                <Route path="pricing" element={<SuperAdminPricingPage />} />
                <Route path="paid-users" element={<SuperAdminPaidUsersPage />} />
                <Route path="admins" element={<SuperAdminAdminsPage />} />
                <Route path="legal" element={<SuperAdminLegalPage />} />
              </Route>
              <Route path="*" element={<Error />} />
              </Routes>
            </Suspense>
            </ErrorBoundary>
          </div>
          </CreateCourseChoiceProvider>
      </Router>
        </OrganizationProvider>
    </LanguageProvider>
  </GlobalErrorHandler>
  );
}

export default App;